import React from "react";
import { BAND_CLASSES } from "./charts/chartTokens";
import { bandOf, fixed, pctText, toNum, topPctN } from "../../../utils/perfFormat";

// Percentile chips for the performance cards (spec B5), in the profile's
// RankBadge look (22px pill, bm-label-xs).
//
//   RankChip({ percentile, kind = "rank" | "descriptive", approximate, band, note, label, className })
//   MedianChip({ value, unit, decimals, prefix = "Med", text, className })
//   topPctLabel(p)   "Top N%" or "Not ranked"
//   pctLabel(p)      "46th pct" or "Not ranked" (a descriptive metric)
//   rankText(p, { kind })  the chip's words for either kind
//
// RankChip
// - "Top N%" with N = max(1, round(100 - p)), rounded as the payload rounds
//   (perfFormat.topPctN), so the chip agrees with the insight text.
// - kind "rank": the filled blue rank chip. kind "descriptive" (pace, 3PA
//   rate, usage, the roster makeup metrics: neither better nor worse):
//   passive, and its words are the plain percentile ("46th pct", "0th pct"
//   for the shortest rotation), never "Top N%", which reads as a quality
//   rank. The cards' legends say what such a chip means.
// - A null percentile reads "Not ranked" (passive), whatever the kind.
// - `approximate` (an approximate peer norm) adds "≈", read out as
//   "approximate".
// - `band` adds the coach snapshot's small band dot: true picks it from the
//   percentile (positive at 60 and up, attention 35 to 59, critical below
//   35); a band name ("positive", "attention", "critical") forces one.
//   Rank chips only: a descriptive metric has no good or bad.
// - `note` prints the peer group size after the chip ("vs 45 squads",
//   perfFormat.peerNote).
// - `label` replaces the chip text while the percentile still picks the
//   style and the band (the coach snapshot's "#27 of 46", "of 46 squads",
//   "61.1% wins"); with no percentile it shows passive.
//
// MedianChip: the passive division median, "Med 0.87", "Med 63.0%".
// Nothing without a value. `unit` "pct" or "%" adds the percent sign; the
// decimals default to 1 ("n2" units: 2), or pass `decimals`. `text` is a
// median already written in its own format ("Med 6'5\"" for a height,
// "Med +3.7" for a signed net) and wins over `value`.

const cx = (...parts) => parts.filter(Boolean).join(" ");

export const NOT_RANKED = "Not ranked";

export const topPctLabel = (p) => {
  const n = topPctN(p);
  return n === null ? NOT_RANKED : `Top ${n}%`;
};

// A descriptive metric's percentile in plain words: "46th pct" (rounded as
// the payload rounds, so it matches the reads' "(46th pct)").
export const pctLabel = (p) => (toNum(p) === null ? NOT_RANKED : pctText(p));

// The words a chip shows for its kind (the phone cells print them as text).
export const rankText = (p, { kind = "rank" } = {}) => (kind === "descriptive" ? pctLabel(p) : topPctLabel(p));

const CHIP = "inline-flex h-[22px] shrink-0 items-center gap-[6px] whitespace-nowrap rounded-full px-[9px] bm-label-xs";
const FILLED = "bg-[#2196f3] text-white";
const PASSIVE = "bg-bm-border text-bm-muted";

const bandName = (band, percentile) => {
  if (!band) return null;
  if (typeof band === "string") return BAND_CLASSES[band] ? band : null;
  return bandOf(percentile);
};

export const RankChip = ({ percentile, kind = "rank", approximate = false, band = false, note, label, className = "" }) => {
  const ranked = toNum(percentile) !== null;
  const filled = ranked && kind !== "descriptive";
  const dot = filled ? bandName(band, percentile) : null;
  const chip = (
    <span className={cx(CHIP, filled ? FILLED : PASSIVE, !note && className)} data-band={dot || undefined}>
      {dot && <span aria-hidden="true" className={cx("h-[7px] w-[7px] shrink-0 rounded-full ring-1 ring-white", BAND_CLASSES[dot])} />}
      {label ?? rankText(percentile, { kind })}
      {ranked && approximate && (
        <>
          <span aria-hidden="true">≈</span>
          <span className="sr-only"> approximate</span>
        </>
      )}
    </span>
  );
  if (!note) return chip;
  return (
    <span className={cx("inline-flex min-w-0 flex-wrap items-center gap-x-[6px] gap-y-1", className)}>
      {chip}
      <span className="bm-body-s text-bm-muted">{note}</span>
    </span>
  );
};

const PERCENT_UNITS = new Set(["pct", "%", "percent"]);

export const medianText = (value, { unit, decimals, prefix = "Med" } = {}) => {
  const v = toNum(value);
  if (v === null) return null;
  const places = decimals ?? (unit === "n2" ? 2 : 1);
  return `${prefix} ${fixed(v, places)}${PERCENT_UNITS.has(unit) ? "%" : ""}`;
};

export const MedianChip = ({ value, unit, decimals, prefix = "Med", text, className = "" }) => {
  const label = (typeof text === "string" && text.trim()) || medianText(value, { unit, decimals, prefix });
  if (!label) return null;
  return <span className={cx(CHIP, PASSIVE, className)}>{label}</span>;
};

export default RankChip;
